'use client'

import { lookup, type Vehicle } from '@cruz-com/lookup'
import { Radio, RadioGroup } from '@headlessui/react'
import { useCookieState, useDebounce } from 'ahooks'
import _ from 'lodash'
import { useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { Divider, H1, LinkButton, LoanItem, NumberFormatInput } from '@/components'
import { toNumber } from '@/lib'
import { defaultLoan, loanRates, loanTerms, pages } from '@/settings'

type Props = {
  initLoan: Vehicle.Loan
}

const Note = () => (
  <p className='text-sm/6'>
    <span className='font-medium'>Note</span>: The calculator does not include taxes, title fee, registration fee, or insurance.
  </p>
)

export const AffordabilityCalculator = ({ initLoan }: Props) => {
  const [loanSettings, setLoanSettings] = useCookieState('loanSettings')
  const [loan, setLoan] = useState<Vehicle.Loan>({
    ...defaultLoan,
    ...initLoan,
    ...JSON.parse(loanSettings || '{}'),
  })
  const [monthlyBudget, setMonthlyBudget] = useState(loan.monthly_budget)
  const [tradeIn, setTradeIn] = useState(loan.trade_in)
  const [downPayment, setDownPayment] = useState(loan.down_payment)
  const [shouldChangeApr, setShouldChangeApr] = useState(!loan?.apr)
  const [apr, setAPR] = useState(loan.apr)

  useEffect(() => {
    const loanSettingsString = JSON.stringify(_.pick(loan, ['monthly_budget', 'trade_in', 'down_payment', 'term', 'credit_score', 'apr']))
    setLoanSettings(loanSettingsString)
  }, [loan.monthly_budget, loan.trade_in, loan.down_payment, loan.term, loan.credit_score, loan.apr, setLoanSettings])

  const getCalculatedValues = () => {
    const { monthly_budget, trade_in, down_payment, term, apr } = loan
    if (!monthly_budget || !term || apr === undefined || apr === null) {
      return {
        maxPrice: 0,
        maxSearchPrice: 0,
        totalLoanAmount: 0,
        totalInterest: 0,
        description: '',
      }
    }
    const monthlyRate = apr / 100 / 12
    const maxLoanAmount = apr === 0 ? monthly_budget * term : Math.round(monthly_budget * ((1 - (1 + monthlyRate) ** -term) / monthlyRate))
    const maxPrice = Math.round(maxLoanAmount + (down_payment || 0) + (trade_in || 0))
    const totalLoanAmount = maxLoanAmount
    const totalPayments = Math.round(monthly_budget * term)
    const totalInterest = totalPayments - maxLoanAmount

    // Find closest price range from lookup (could be smaller or larger)
    const maxSearchPrice = _.minBy(lookup.vehicle.lkpPrice, (price) => Math.abs(price - maxPrice)) || 0

    return {
      maxPrice: Math.max(0, maxPrice),
      maxSearchPrice: Math.max(0, maxSearchPrice),
      totalLoanAmount: Math.max(0, totalLoanAmount),
      totalInterest: Math.max(0, totalInterest),
      description: `${term} months, ${apr}% APR`,
    }
  }

  const { maxPrice, maxSearchPrice, totalLoanAmount, totalInterest, description } = getCalculatedValues()

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select()
  }

  // filed changed
  const handleLoanFieldChange = (field: keyof Vehicle.Loan, value: string | number) => {
    if (field === 'condition') setShouldChangeApr(true)
    setLoan((prev: Vehicle.Loan) => ({ ...prev, [field]: value }))
  }

  // budget
  const handleBudgetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMonthlyBudget(toNumber(e.target.value) || 0)
  }

  const dbBudget = useDebounce(monthlyBudget, { wait: 300 })
  useEffect(() => {
    setLoan((prev: Vehicle.Loan) => ({ ...prev, monthly_budget: dbBudget }))
  }, [dbBudget])

  // trade-in
  const handleTradeInChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTradeIn(toNumber(e.target.value) || 0)
  }

  const dbTradeIn = useDebounce(tradeIn, { wait: 300 })
  useEffect(() => {
    setLoan((prev: Vehicle.Loan) => ({ ...prev, trade_in: dbTradeIn }))
  }, [dbTradeIn])

  // down payment
  const handleDownPaymentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDownPayment(toNumber(e.target.value) || 0)
  }

  const dbDownPayment = useDebounce(downPayment, { wait: 300 })
  useEffect(() => {
    setLoan((prev: Vehicle.Loan) => ({ ...prev, down_payment: dbDownPayment }))
  }, [dbDownPayment])

  // credit score changed
  const handleCreditScoreChange = (val: Vehicle.Loan['credit_score']) => {
    setShouldChangeApr(true)
    setLoan((prev: Vehicle.Loan) => ({ ...prev, credit_score: val }))
  }

  // apr
  function getApr(apr: number | null, creditScore: string | undefined, condition: string | undefined) {
    if (apr) {
      return apr
    }
    const score = (creditScore || 'Great') as keyof typeof loanRates
    const cond = (condition || 'used') as 'new' | 'used'
    return loanRates[score]?.[cond] || 0
  }

  const handleAPRChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShouldChangeApr(false)
    setAPR(toNumber(e.target.value) || 0)
  }

  const dbApr = useDebounce(apr, { wait: 300 })
  useEffect(() => {
    setLoan((prev: Vehicle.Loan) => ({ ...prev, apr: dbApr }))
  }, [dbApr])

  useEffect(() => {
    if (shouldChangeApr) {
      setAPR(getApr(null, loan.credit_score, loan.condition))
    }
  }, [loan.credit_score, loan.condition, shouldChangeApr])

  const Result = (
    <section className='mx-auto max-w-sm space-y-3' aria-live='polite'>
      <h2 className='my-0! text-center text-sm! font-medium!'>Estimated Maximum Vehicle Price You Can Afford</h2>
      <div className='font-price text-center text-4xl font-semibold'>
        {maxPrice >= 0 ? (
          <>
            <sup className='mr-1 text-xl'>$</sup>
            {maxPrice?.toLocaleString()}
          </>
        ) : (
          <>-</>
        )}
      </div>
      {!_.isEmpty(description) && <div className='text-center text-base font-normal'>{description}</div>}
      <div className='text-sm'>
        <div className='mt-6 hidden divide-y border-t text-sm md:block'>
          <LoanItem label='Monthly Budget' value={loan?.monthly_budget ? `$${loan?.monthly_budget?.toLocaleString()}` : '-'} />
          <LoanItem label='Trade-in Value' value={loan.trade_in ? `-$${loan?.trade_in?.toLocaleString()}` : '-'} />
          <LoanItem label='Down Payment' value={loan.down_payment ? `-$${loan?.down_payment?.toLocaleString()}` : '-'} />
        </div>
        <div className='divide-y border-t-4 border-zinc-100 dark:border-zinc-700'>
          <LoanItem label='Loan Amount' value={totalLoanAmount ? `$${totalLoanAmount.toLocaleString()}` : '-'} />
          <LoanItem label='Total Interest' value={totalInterest ? `+$${totalInterest.toLocaleString()}` : '-'} />
          <LoanItem
            label='Total Payment'
            className='font-bold!'
            value={totalInterest + totalLoanAmount > 0 ? `$${(totalInterest + totalLoanAmount).toLocaleString()}` : '-'}
          />
        </div>
      </div>
      <div className='hidden md:mt-6 md:block'>
        <Note />
      </div>
      {maxSearchPrice > 0 && (
        <div className='my-6 flex justify-center'>
          <LinkButton
            href={`/vehicles/search?price_max=${maxSearchPrice}&condition=${loan.condition === 'new' ? '1' : encodeURIComponent('2,3')}`}
            variant='primary'
            className='w-fit rounded-full px-6'
            label='Shop Vehicles in Your Budget'
            icon='search'
          />
        </div>
      )}
    </section>
  )

  return (
    <>
      <H1>{pages.affordabilityCalculator.name}</H1>
      <Divider />
      <p className='mt-6 text-sm/6 font-normal'>
        Use this calculator to determine how much you can comfortably afford to spend on a vehicle based on your monthly income, expenses,
        and desired down payment. This tool helps you set a realistic budget before you start shopping for your next car.
      </p>
      <section className='mt-4 grid rounded border md:grid-cols-3 md:divide-x md:divide-y-0'>
        <div className='order-2 gap-4 space-y-6 p-4 sm:p-6 md:order-1 md:col-span-2 md:p-8'>
          <div>
            <label htmlFor='condition' className='form-label'>
              Condition
            </label>
            <RadioGroup
              name='condition'
              value={loan.condition}
              onChange={(val) => handleLoanFieldChange('condition', val)}
              className='radio-group grid w-fit grid-cols-2'
            >
              <Radio value='new' className={twMerge('radio px-4! font-medium!', loan?.condition === 'new' && 'radio-checked')}>
                New
              </Radio>
              <Radio value='used' className={twMerge('radio px-4! font-medium!', loan?.condition === 'used' && 'radio-checked')}>
                Used
              </Radio>
            </RadioGroup>
          </div>
          <div className='grid grid-cols-3 gap-4'>
            <NumberFormatInput
              id='budget'
              label='Monthly Budget'
              prepend='$'
              maxLength={8}
              value={monthlyBudget || 0}
              onFocus={handleInputFocus}
              onChange={handleBudgetChange}
            />
            <NumberFormatInput
              id='trade_in'
              label='Trade-in Value'
              prepend='$'
              maxLength={8}
              value={tradeIn || 0}
              onFocus={handleInputFocus}
              onChange={handleTradeInChange}
            />
            <NumberFormatInput
              id='down_payment'
              label='Down Payment'
              prepend='$'
              maxLength={8}
              value={downPayment || 0}
              onFocus={handleInputFocus}
              onChange={handleDownPaymentChange}
            />
          </div>
          <div>
            <label htmlFor='term' className='form-label'>
              Loan Term <span className='font-light'>(months)</span>
            </label>
            <RadioGroup
              name='term'
              value={loan.term}
              onChange={(val) => handleLoanFieldChange('term', val)}
              className='radio-group grid w-full grid-cols-6'
            >
              {_.map(loanTerms, (d) => {
                return (
                  <Radio key={`term_${d}`} value={d} className={twMerge('radio', loan.term === d && 'radio-checked')}>
                    <div className='space-x-1'>
                      <span className='text-base font-medium'>{d}</span>
                      <span className='block text-xs font-light sm:inline-block'>mo.</span>
                    </div>
                  </Radio>
                )
              })}
            </RadioGroup>
          </div>
          <div>
            <label htmlFor='credit_score' className='form-label'>
              Your Credit Score
            </label>
            <RadioGroup
              name='credit_score'
              value={loan.credit_score}
              onChange={(val) => handleCreditScoreChange(val)}
              className='radio-group grid w-full grid-cols-5'
            >
              {_.map(_.keys(loanRates), (key) => {
                const d = _.get(loanRates, key)
                return (
                  <Radio key={`cs_${key}`} value={key} className={twMerge('radio', loan.credit_score === key && 'radio-checked')}>
                    <div className='text-sm font-medium md:text-base'>{key}</div>
                    <div className='mt-1 text-center text-xs font-light md:text-sm'>{d.score}</div>
                    <div className='mt-1 text-center text-xs font-light md:text-sm'>
                      APR: <span className='font-semibold'>{_.get(d, loan.condition)}%</span>
                    </div>
                  </Radio>
                )
              })}
            </RadioGroup>
          </div>
          <div className='grid grid-cols-3 gap-4'>
            <NumberFormatInput
              id='apr'
              label='APR'
              addon='%'
              maxLength={5}
              value={apr || 0}
              onFocus={handleInputFocus}
              onChange={handleAPRChange}
            />
          </div>
        </div>
        <div className='order-1 w-full grow border-b px-8 pt-6 pb-2 md:order-2 md:border-b-0'>{Result}</div>
      </section>
      <section className='mt-8 space-y-4 text-sm'>
        <div className='md:hidden'>
          <Note />
        </div>
        <p>
          <span className='font-medium'>Disclaimer</span>: This interactive auto affordability calculator and all information provided are
          for illustrative purposes only. The accuracy of results and ability to secure financing are not guaranteed.
        </p>
      </section>
    </>
  )
}
